import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  School, Users, BarChart3, Plus, Download, Copy, Check,
  ChevronRight, Trophy, Target, Clock, BookOpen, Sparkles
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [school, setSchool] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classAnalytics, setClassAnalytics] = useState(null);
  const [showCreateSchool, setShowCreateSchool] = useState(false);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [showAddPupils, setShowAddPupils] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState('');
  const [newClassName, setNewClassName] = useState('');
  const [newYearGroup, setNewYearGroup] = useState(3);
  const [pupilNames, setPupilNames] = useState('');
  const [createdPupils, setCreatedPupils] = useState([]);
  const [copiedCode, setCopiedCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const userRes = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userRes.data);
      
      if (userRes.data.school_id) {
        // Load school and classes
        const [schoolRes, classesRes] = await Promise.all([
          axios.get(`${API}/api/teacher/school`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/teacher/classes`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setSchool(schoolRes.data);
        setClasses(classesRes.data.classes || []);
        
        if (classesRes.data.classes?.length > 0) {
          setSelectedClass(classesRes.data.classes[0]);
          loadClassAnalytics(classesRes.data.classes[0].id);
        }
      } else {
        setShowCreateSchool(true);
      }
    } catch (e) {
      if (e.response?.status === 401) {
        navigate('/login');
      }
    }
    setLoading(false);
  };

  const loadClassAnalytics = async (classId) => {
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.get(`${API}/api/teacher/class/${classId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassAnalytics(res.data);
    } catch (e) {
      console.error('Failed to load analytics');
    }
  };

  const createSchool = async () => {
    if (!newSchoolName.trim()) return;
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.post(`${API}/api/teacher/school`,
        { name: newSchoolName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSchool({ id: res.data.school_id, code: res.data.school_code, name: res.data.school_name });
      setShowCreateSchool(false);
      setSuccess('School created successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create school');
    }
    setLoading(false);
  };

  const createClass = async () => {
    if (!newClassName.trim()) return;
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/api/teacher/classes`,
        { name: newClassName, year_group: newYearGroup },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowCreateClass(false);
      setNewClassName('');
      setSuccess('Class created!');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create class');
    }
    setLoading(false);
  };

  const addPupils = async () => {
    if (!pupilNames.trim() || !selectedClass) return;
    setLoading(true);
    setError('');
    
    // Parse names (one per line or comma-separated)
    const names = pupilNames.split(/[\n,]/).map(n => n.trim()).filter(n => n);
    if (names.length === 0) {
      setError('Please enter at least one name');
      setLoading(false);
      return;
    }
    
    if (names.length > 30) {
      setError('Maximum 30 pupils per class');
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.post(`${API}/api/teacher/class/${selectedClass.id}/bulk-pupils`,
        { 
          class_id: selectedClass.id,
          pupils: names.map(name => ({ name }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreatedPupils(res.data.pupils);
      setPupilNames('');
      setSuccess(`${res.data.pupils_created} pupils added!`);
      setTimeout(() => setSuccess(''), 5000);
      loadClassAnalytics(selectedClass.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add pupils');
    }
    setLoading(false);
  };

  const exportClass = async () => {
    if (!selectedClass) return;
    
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.get(`${API}/api/teacher/class/${selectedClass.id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `class_${selectedClass.name}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError('Export failed');
    }
  };

  const copySchoolCode = () => {
    if (school?.code) {
      navigator.clipboard.writeText(school.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const createChallenge = async () => {
    if (!selectedClass) return;
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/api/teacher/class/${selectedClass.id}/challenge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Challenge created! Pupils will be notified.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Failed to create challenge');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 to-mint-green/20 flex items-center justify-center">
        <div className="text-gray-600 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-mint-green/20">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-100 border border-green-300 text-green-700 px-4 py-3 rounded-xl">
            {success}
          </div>
        )}

        {/* Create School Modal */}
        {showCreateSchool && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full">
              <School className="w-12 h-12 text-sky-blue mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-center mb-2">Create Your School</h2>
              <p className="text-gray-600 text-center mb-6">
                Set up your school to start adding classes and pupils
              </p>
              <input
                type="text"
                placeholder="School name (e.g., St Mary's Primary)"
                value={newSchoolName}
                onChange={(e) => setNewSchoolName(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl mb-4 focus:outline-none focus:border-sky-blue"
              />
              <button
                onClick={createSchool}
                disabled={!newSchoolName.trim()}
                className="w-full py-3 bg-sky-blue text-white rounded-xl font-semibold hover:bg-sky-blue/90 disabled:opacity-50 transition-colors"
              >
                Create School
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {school && (
          <>
            {/* School Header */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <School className="w-8 h-8 text-sky-blue" />
                    <h1 className="text-2xl font-bold text-gray-900">{school.name}</h1>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">School Code:</span>
                    <code className="bg-gray-100 px-3 py-1 rounded-lg font-mono text-lg">{school.code}</code>
                    <button
                      onClick={copySchoolCode}
                      className="p-2 text-gray-400 hover:text-sky-blue transition-colors"
                    >
                      {copiedCode ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateClass(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-mint-green text-white rounded-xl font-medium hover:bg-mint-green/90 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Class
                  </button>
                </div>
              </div>
            </div>

            {/* Create Class Modal */}
            {showCreateClass && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-8 max-w-md w-full">
                  <h2 className="text-xl font-bold mb-4">Create New Class</h2>
                  <input
                    type="text"
                    placeholder="Class name (e.g., 3B, Year 4 Tigers)"
                    value={newClassName}
                    onChange={(e) => setNewClassName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl mb-4 focus:outline-none focus:border-mint-green"
                  />
                  <div className="mb-4">
                    <label className="block text-gray-600 mb-2">Year Group</label>
                    <select
                      value={newYearGroup}
                      onChange={(e) => setNewYearGroup(parseInt(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-mint-green"
                    >
                      {[1,2,3,4,5,6].map(y => (
                        <option key={y} value={y}>Year {y}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowCreateClass(false)}
                      className="flex-1 py-3 border-2 border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createClass}
                      disabled={!newClassName.trim()}
                      className="flex-1 py-3 bg-mint-green text-white rounded-xl font-semibold hover:bg-mint-green/90 disabled:opacity-50 transition-colors"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Classes Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Class List */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <h2 className="font-semibold text-gray-900 mb-4">Your Classes</h2>
                  <div className="space-y-2">
                    {classes.map((cls) => (
                      <button
                        key={cls.id}
                        onClick={() => { setSelectedClass(cls); loadClassAnalytics(cls.id); }}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                          selectedClass?.id === cls.id 
                            ? 'bg-sky-blue text-white' 
                            : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div>
                          <div className="font-medium">{cls.name}</div>
                          <div className={`text-sm ${selectedClass?.id === cls.id ? 'text-white/70' : 'text-gray-500'}`}>
                            Year {cls.year_group}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    ))}
                    {classes.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No classes yet</p>
                        <button
                          onClick={() => setShowCreateClass(true)}
                          className="mt-2 text-sky-blue font-medium"
                        >
                          Create your first class
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Class Details */}
              <div className="lg:col-span-3">
                {selectedClass && classAnalytics ? (
                  <div className="space-y-6">
                    {/* Class Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-2xl p-4 shadow-sm">
                        <Users className="w-8 h-8 text-sky-blue mb-2" />
                        <div className="text-3xl font-bold text-gray-900">{classAnalytics.total_pupils}</div>
                        <div className="text-gray-500">Pupils</div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 shadow-sm">
                        <Target className="w-8 h-8 text-mint-green mb-2" />
                        <div className="text-3xl font-bold text-gray-900">{classAnalytics.average_accuracy}%</div>
                        <div className="text-gray-500">Avg Accuracy</div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 shadow-sm">
                        <Clock className="w-8 h-8 text-coral-orange mb-2" />
                        <div className="text-3xl font-bold text-gray-900">{classAnalytics.total_sessions}</div>
                        <div className="text-gray-500">Total Sessions</div>
                      </div>
                      <div className="bg-white rounded-2xl p-4 shadow-sm">
                        <BarChart3 className="w-8 h-8 text-lavender mb-2" />
                        <div className="text-3xl font-bold text-gray-900">{classAnalytics.total_keystrokes.toLocaleString()}</div>
                        <div className="text-gray-500">Keystrokes</div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => setShowAddPupils(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-mint-green text-white rounded-xl font-medium hover:bg-mint-green/90 transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                        Add Pupils
                      </button>
                      <button
                        onClick={exportClass}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                      >
                        <Download className="w-5 h-5" />
                        Export CSV
                      </button>
                      <button
                        onClick={createChallenge}
                        className="flex items-center gap-2 px-4 py-2 bg-coral-orange text-white rounded-xl font-medium hover:bg-coral-orange/90 transition-colors"
                      >
                        <Sparkles className="w-5 h-5" />
                        Set Challenge
                      </button>
                    </div>

                    {/* Add Pupils Modal */}
                    {showAddPupils && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto">
                          <h2 className="text-xl font-bold mb-2">Add Pupils to {selectedClass.name}</h2>
                          <p className="text-gray-600 mb-4">Enter pupil names, one per line or comma-separated</p>
                          
                          <textarea
                            placeholder="John Smith&#10;Emma Jones&#10;Oliver Wilson"
                            value={pupilNames}
                            onChange={(e) => setPupilNames(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl mb-4 focus:outline-none focus:border-mint-green h-40 resize-none"
                          />
                          
                          {createdPupils.length > 0 && (
                            <div className="mb-4 bg-green-50 rounded-xl p-4">
                              <h3 className="font-semibold text-green-800 mb-2">Pupil Credentials</h3>
                              <p className="text-sm text-green-700 mb-3">Share these with pupils to log in:</p>
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {createdPupils.map((p) => (
                                  <div key={p.id} className="bg-white rounded-lg p-2 text-sm flex justify-between">
                                    <span className="font-medium">{p.name}</span>
                                    <code className="text-gray-600">{p.username} / {p.password}</code>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="flex gap-3">
                            <button
                              onClick={() => { setShowAddPupils(false); setCreatedPupils([]); }}
                              className="flex-1 py-3 border-2 border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                            >
                              {createdPupils.length > 0 ? 'Done' : 'Cancel'}
                            </button>
                            {createdPupils.length === 0 && (
                              <button
                                onClick={addPupils}
                                disabled={!pupilNames.trim()}
                                className="flex-1 py-3 bg-mint-green text-white rounded-xl font-semibold hover:bg-mint-green/90 disabled:opacity-50 transition-colors"
                              >
                                Add Pupils
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pupils Table */}
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-900">Pupils ({classAnalytics.total_pupils}/30)</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-6 py-3 text-gray-500 font-medium">Name</th>
                              <th className="text-left px-6 py-3 text-gray-500 font-medium">Username</th>
                              <th className="text-left px-6 py-3 text-gray-500 font-medium">Stage</th>
                              <th className="text-left px-6 py-3 text-gray-500 font-medium">Level</th>
                              <th className="text-left px-6 py-3 text-gray-500 font-medium">Accuracy</th>
                              <th className="text-left px-6 py-3 text-gray-500 font-medium">Sessions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {classAnalytics.pupils.map((pupil) => (
                              <tr key={pupil.id} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-6 py-4 font-medium text-gray-900">{pupil.name}</td>
                                <td className="px-6 py-4 text-gray-600 font-mono text-sm">{pupil.username}</td>
                                <td className="px-6 py-4">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    pupil.stage >= 4 ? 'bg-amber-100 text-amber-700' :
                                    pupil.stage >= 2 ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    Stage {pupil.stage}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-gray-900">{pupil.level}</td>
                                <td className="px-6 py-4">
                                  <span className={`font-medium ${
                                    pupil.accuracy >= 90 ? 'text-green-600' :
                                    pupil.accuracy >= 70 ? 'text-amber-600' :
                                    'text-red-600'
                                  }`}>
                                    {pupil.accuracy}%
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-gray-600">{pupil.total_sessions}</td>
                              </tr>
                            ))}
                            {classAnalytics.pupils.length === 0 && (
                              <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                  No pupils in this class yet
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Stage Distribution */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <h3 className="font-semibold text-gray-900 mb-4">Stage Distribution</h3>
                      <div className="flex gap-4">
                        {Object.entries(classAnalytics.stage_distribution).map(([stage, count]) => (
                          <div key={stage} className="flex-1">
                            <div className="text-center mb-2">
                              <div className="text-2xl font-bold text-gray-900">{count}</div>
                              <div className="text-sm text-gray-500">Stage {stage}</div>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-sky-blue rounded-full"
                                style={{ width: `${classAnalytics.total_pupils > 0 ? (count / classAnalytics.total_pupils) * 100 : 0}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
                    <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Class</h3>
                    <p className="text-gray-500">Choose a class from the sidebar to view analytics</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default TeacherDashboard;
